import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  RefreshCw, CheckCircle, XCircle, AlertTriangle, Loader2,
  Users, LayoutDashboard, Activity, ChevronDown, ClipboardList,
  Building2, Crown, ShieldAlert, Search, CalendarDays, Mail, Phone, X, Eye, MessageSquare, Check, Clock, Copy, KeyRound, Trash2,
} from "lucide-react";

//  Status / plan config

const STATUS_CONFIG = {
  active:    { label: "Ativo",     color: "text-green-400",  bg: "bg-green-400/10",  border: "border-green-400/30"  },
  trial:     { label: "Trial",     color: "text-blue-400",   bg: "bg-blue-400/10",   border: "border-blue-400/30"   },
  inactive:  { label: "Inativo",   color: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/30" },
  blocked:   { label: "Bloqueado", color: "text-red-400",    bg: "bg-red-400/10",    border: "border-red-400/30"    },
  cancelled: { label: "Cancelado", color: "text-slate-400",  bg: "bg-slate-400/10",  border: "border-slate-400/30"  },
};

const PLAN_CONFIG = {
  free:       { label: "Free",       color: "text-slate-400" },
  pro:        { label: "Pro",        color: "text-blue-400"  },
  enterprise: { label: "Enterprise", color: "text-amber-400" },
};

const HEALTH_STATUS = {
  ok:      { icon: CheckCircle,   color: "text-green-400",  bg: "bg-green-400/10",  border: "border-green-400/20",  label: "Operacional" },
  warning: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/20", label: "Ateno"     },
  error:   { icon: XCircle,       color: "text-red-400",    bg: "bg-red-400/10",    border: "border-red-400/20",    label: "Erro"        },
};

const ACTION_LABELS = {
  "tenant.status.active":    "Cliente ativado",
  "tenant.status.inactive":  "Cliente inativado",
  "tenant.status.blocked":   "Cliente bloqueado",
  "tenant.status.cancelled": "Cliente cancelado",
  "tenant.status.trial":     "Cliente em trial",
  "tenant.plan.changed":     "Plano alterado",
  "tenant.cancelled":        "Cliente cancelado",
  "demo_request.status.changed": "Status da solicitação alterado",
};

const DEMO_STATUS_CONFIG = {
  novo:          { label: "Novo",          color: "text-blue-400",   bg: "bg-blue-400/10",   border: "border-blue-400/30" },
  em_contato:    { label: "Em contato",    color: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/30" },
  demo_agendada: { label: "Demo agendada", color: "text-purple-400", bg: "bg-purple-400/10", border: "border-purple-400/30" },
  aprovado:      { label: "Aprovado",      color: "text-green-400",  bg: "bg-green-400/10",  border: "border-green-400/30" },
  rejeitado:     { label: "Rejeitado",     color: "text-red-400",    bg: "bg-red-400/10",    border: "border-red-400/30" },
  convertido:    { label: "Convertido",    color: "text-cyan-400",   bg: "bg-cyan-400/10",   border: "border-cyan-400/30" },
};

const FEATURE_OPTIONS = [
  { key: "meta_official_integrations", label: "Integracoes Meta oficiais", description: "Grupo dos canais oficiais Meta no CRM." },
  { key: "whatsapp_official_meta", label: "WhatsApp Oficial Meta", description: "Conexao e configuracao do WhatsApp Cloud API." },
  { key: "instagram_direct", label: "Instagram Direct", description: "Conexao e configuracao de DMs do Instagram." },
  { key: "facebook_messenger", label: "Facebook Messenger", description: "Conexao e configuracao de paginas do Facebook." },
  { key: "external_integrations", label: "Integracoes externas", description: "Grupo de API, webhooks e documentacao externa." },
  { key: "api_keys", label: "Chaves de API", description: "Geracao e gerenciamento de chaves REST API." },
  { key: "webhooks", label: "Webhooks", description: "Configuracao de webhooks outbound." },
  { key: "api_docs", label: "Documentacao da API", description: "Exibicao da documentacao de integracao." },
  { key: "advanced_channel_settings", label: "Configuracoes avancadas", description: "Paineis avancados de canais oficiais." },
];

//  Reusable components

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

function StatCard({ label, value, color = "text-slate-100" }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
      <div className={`text-3xl font-bold ${color}`}>{value ?? ""}</div>
      <div className="text-slate-400 text-sm mt-1">{label}</div>
    </div>
  );
}

function Btn({ label, color, disabled, onClick }) {
  const cls = {
    green:  "text-green-400 bg-green-400/10 hover:bg-green-400/20 border-green-400/30",
    yellow: "text-yellow-400 bg-yellow-400/10 hover:bg-yellow-400/20 border-yellow-400/30",
    red:    "text-red-400 bg-red-400/10 hover:bg-red-400/20 border-red-400/30",
    slate:  "text-slate-400 bg-slate-400/10 hover:bg-slate-400/20 border-slate-400/30",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-2 py-1 text-xs rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${cls[color]}`}
    >
      {label}
    </button>
  );
}


function ConfirmDeleteModal({ open, title, description, expectedText, onClose, onConfirm, busy = false }) {
  const [confirmation, setConfirmation] = useState("");
  useEffect(() => { if (open) setConfirmation(""); }, [open]);
  if (!open) return null;
  const canConfirm = confirmation.trim() === expectedText;
  return <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/75 px-4" onClick={busy ? undefined : onClose}><div className="w-full max-w-md rounded-xl border border-red-400/30 bg-slate-950 shadow-2xl" onClick={(e) => e.stopPropagation()}><div className="border-b border-slate-800 px-6 py-5"><h3 className="text-lg font-semibold text-red-400">{title}</h3><p className="mt-2 text-sm leading-relaxed text-slate-400">{description}</p></div><div className="space-y-3 px-6 py-5"><p className="text-sm text-slate-300">Digite <strong className="text-slate-100">{expectedText}</strong> para confirmar.</p><input autoFocus value={confirmation} onChange={(e) => setConfirmation(e.target.value)} className="h-10 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 focus:border-red-400 focus:outline-none" /></div><div className="flex justify-end gap-2 border-t border-slate-800 px-6 py-4"><button disabled={busy} onClick={onClose} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-900 disabled:opacity-60">Cancelar</button><button disabled={!canConfirm || busy} onClick={onConfirm} className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Excluir definitivamente</button></div></div></div>;
}

function PlanSelect({ currentPlan, tenantId, disabled, onUpdated }) {
  const [open, setOpen] = useState(false);
  const plans = ["free", "pro", "enterprise"];

  const handleSelect = async (plan) => {
    setOpen(false);
    if (plan === currentPlan) return;
    try {
      await api.patch(`/admin/tenants/${tenantId}/plan`, { plan });
      toast.success(`Plano alterado para ${PLAN_CONFIG[plan]?.label || plan}`);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alterar plano.");
    }
  };

  return (
    <div className="relative inline-block">
      <button
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-700 text-slate-300 bg-slate-800 hover:bg-slate-700 transition-colors disabled:opacity-40"
      >
        <span className={PLAN_CONFIG[currentPlan]?.color || "text-slate-400"}>
          {PLAN_CONFIG[currentPlan]?.label || currentPlan}
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl min-w-[110px]">
            {plans.map((plan) => (
              <button
                key={plan}
                onClick={() => handleSelect(plan)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${PLAN_CONFIG[plan]?.color}`}
              >
                {PLAN_CONFIG[plan]?.label}{plan === currentPlan && " "}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

//  Clientes tab

function TenantFeaturesModal({ tenant, open, saving, onClose, onSave }) {
  const [features, setFeatures] = useState({});

  useEffect(() => {
    if (open) setFeatures(tenant?.features || {});
  }, [open, tenant]);

  if (!open || !tenant) return null;

  const toggleFeature = (key) => {
    setFeatures((current) => ({ ...current, [key]: !current?.[key] }));
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/75 px-4" onClick={saving ? undefined : onClose}>
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Funcionalidades do cliente</p>
            <h3 className="mt-1 text-lg font-semibold text-slate-100">{tenant.displayName || tenant.company_name || tenant.owner_email}</h3>
            <p className="mt-1 text-sm text-slate-500">Libere recursos por tenant sem alterar codigo ou reimplantar o CRM.</p>
          </div>
          <button disabled={saving} onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-900 hover:text-slate-200 disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[62vh] overflow-y-auto px-6 py-5">
          <div className="grid gap-3 sm:grid-cols-2">
            {FEATURE_OPTIONS.map((feature) => {
              const enabled = Boolean(features?.[feature.key]);
              return (
                <button
                  key={feature.key}
                  type="button"
                  onClick={() => toggleFeature(feature.key)}
                  className={`rounded-xl border p-4 text-left transition ${enabled ? "border-emerald-400/30 bg-emerald-400/10" : "border-slate-800 bg-slate-900/70 hover:border-slate-700"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-100">{feature.label}</p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-500">{feature.description}</p>
                    </div>
                    <span className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full border transition ${enabled ? "border-emerald-400/40 bg-emerald-400/30" : "border-slate-700 bg-slate-800"}`}>
                      <span className={`absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white transition ${enabled ? "left-5" : "left-0.5"}`} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-800 px-6 py-4">
          <button disabled={saving} onClick={onClose} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-900 disabled:opacity-60">Cancelar</button>
          <button disabled={saving} onClick={() => onSave(features)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar recursos
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientesTab({ stats, clientes, onReload }) {
  const [updating, setUpdating] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [featuresTarget, setFeaturesTarget] = useState(null);
  const [savingFeatures, setSavingFeatures] = useState(false);

  const changeStatus = async (tenantId, newStatus, label) => {
    setUpdating(tenantId);
    try {
      await api.patch(`/admin/tenants/${tenantId}/status`, { tenant_status: newStatus });
      toast.success(`Cliente ${label} com sucesso.`);
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar status.");
    } finally {
      setUpdating(null);
    }
  };

  const cancelCliente = async (tenantId, name) => {
    if (!window.confirm(`Cancelar a conta de "${name}"?\n\nIsto bloqueará o acesso imediatamente e não pode ser desfeito facilmente.`)) return;
    setUpdating(tenantId);
    try {
      await api.post(`/admin/tenants/${tenantId}/cancel`);
      toast.success("Conta cancelada.");
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar.");
    } finally {
      setUpdating(null);
    }
  };

  const deleteCliente = async () => {
    if (!deleteTarget) return;
    setUpdating(deleteTarget.tenant_id);
    try {
      await api.delete("/admin/tenants/" + deleteTarget.tenant_id);
      toast.success("Cliente excluido definitivamente.");
      setDeleteTarget(null);
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir cliente.");
    } finally {
      setUpdating(null);
    }
  };

  const confirmBlock = async (tenantId, name) => {
    if (!window.confirm(`Bloquear acesso de "${name}"?\n\nO cliente não poderá acessar o sistema at ser áreativado.`)) return;
    await changeStatus(tenantId, "blocked", "bloqueado");
  };

  const saveTenantFeatures = async (features) => {
    if (!featuresTarget?.tenant_id) return;
    setSavingFeatures(true);
    try {
      const response = await api.patch(`/admin/tenants/${featuresTarget.tenant_id}/features`, { features });
      const updatedFeatures = response.data?.features || features;
      toast.success("Recursos atualizados com sucesso.");
      setFeaturesTarget((current) => current ? { ...current, features: updatedFeatures } : current);
      await onReload();
      setFeaturesTarget(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao atualizar recursos do cliente.");
    } finally {
      setSavingFeatures(false);
    }
  };

  return (
    <>
      <ConfirmDeleteModal open={Boolean(deleteTarget)} title="Excluir cliente definitivamente" description="Esta acao e irreversivel. O workspace sera removido da listagem e seus usuarios serao desativados." expectedText={deleteTarget?.displayName || "EXCLUIR"} onClose={() => setDeleteTarget(null)} onConfirm={deleteCliente} busy={Boolean(deleteTarget && updating === deleteTarget.tenant_id)} />
      <TenantFeaturesModal tenant={featuresTarget} open={Boolean(featuresTarget)} saving={savingFeatures} onClose={() => setFeaturesTarget(null)} onSave={saveTenantFeatures} />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          <StatCard label="Total de Clientes" value={stats.tenants?.total} />
          <StatCard label="Ativos"     value={stats.tenants?.active}    color="text-green-400"  />
          <StatCard label="Trial"      value={stats.tenants?.trial}     color="text-blue-400"   />
          <StatCard label="Inativos"   value={stats.tenants?.inactive}  color="text-yellow-400" />
          <StatCard label="Bloqueados" value={stats.tenants?.blocked}   color="text-red-400"    />
          <StatCard label="Cancelados" value={stats.tenants?.cancelled} color="text-slate-400"  />
          <StatCard label="Usurios"   value={stats.total_users} />
          <StatCard label="Leads"      value={stats.total_leads} />
        </div>
      )}

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="font-medium flex items-center gap-2 text-slate-100">
            <Building2 className="w-4 h-4 text-slate-400" />
            Clientes ({clientes.length})
          </h2>
          <button
            onClick={onReload}
            className="text-xs text-slate-400 hover:text-slate-100 transition-colors flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Atualizar
          </button>
        </div>

        <div className="max-h-[calc(100vh-330px)] min-h-[320px] overflow-auto">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-900">
              <tr className="text-slate-500 text-xs border-b border-slate-800 uppercase tracking-wide shadow-[0_1px_0_rgba(30,41,59,0.9)]">
                <th className="text-left px-5 py-3">Empresa / Owner</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Plano</th>
                <th className="text-left px-5 py-3">Usurios</th>
                <th className="text-left px-5 py-3">Leads</th>
                <th className="text-left px-5 py-3">Canais</th>
                <th className="text-left px-5 py-3">Criado em</th>
                <th className="text-left px-5 py-3 min-w-[250px]">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {clientes.map((c) => {
                const busy = updating === c.tenant_id;
                const displayName = c.company_name || c.owner_name || c.owner_email;
                return (
                  <tr key={c.tenant_id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-5 py-4 min-w-[250px]">
                      <div className="font-medium text-slate-100 flex items-center gap-1.5">
                        {displayName}
                      </div>
                      {c.company_name && (
                        <div className="text-slate-400 text-xs mt-0.5">{c.owner_name}</div>
                      )}
                      <div className="text-slate-500 text-xs mt-0.5">{c.owner_email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <StatusBadge status={c.tenant_status} />
                    </td>
                    <td className="px-5 py-4">
                      <PlanSelect
                        currentPlan={c.plan || "free"}
                        tenantId={c.tenant_id}
                        disabled={busy}
                        onUpdated={onReload}
                      />
                    </td>
                    <td className="px-5 py-4 text-slate-300">{c.user_count ?? 0}</td>
                    <td className="px-5 py-4 text-slate-300">{c.lead_count ?? 0}</td>
                    <td className="px-5 py-4 text-slate-300">{c.channels_connected ?? 0}</td>
                    <td className="px-5 py-4 text-slate-500 text-xs">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : ""}
                    </td>
                    <td className="px-5 py-4">
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                      ) : (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Btn label="Recursos" color="slate" onClick={() => setFeaturesTarget({ ...c, displayName })} />
                          {c.tenant_status !== "active" && (
                            <Btn label="Ativar" color="green" onClick={() => changeStatus(c.tenant_id, "active", "ativado")} />
                          )}
                          {c.tenant_status !== "trial" && c.tenant_status !== "cancelled" && (
                            <Btn label="Trial" color="green" onClick={() => changeStatus(c.tenant_id, "trial", "colocado em trial")} />
                          )}
                          {c.tenant_status !== "inactive" && c.tenant_status !== "cancelled" && (
                            <Btn label="Inativar" color="yellow" onClick={() => changeStatus(c.tenant_id, "inactive", "inativado")} />
                          )}
                          {c.tenant_status !== "blocked" && c.tenant_status !== "cancelled" && (
                            <Btn label="Bloquear" color="red" onClick={() => confirmBlock(c.tenant_id, displayName)} />
                          )}
                          {c.tenant_status !== "cancelled" && (
                            <Btn label="Cancelar" color="slate" onClick={() => cancelCliente(c.tenant_id, displayName)} />
                          )}
                          {["inactive", "cancelled"].includes(c.tenant_status) && (
                            <Btn label="Excluir definitivamente" color="red" onClick={() => setDeleteTarget({ ...c, displayName })} />
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {clientes.length === 0 && (
            <div className="text-center py-16">
              <Building2 className="w-10 h-10 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Nenhum cliente encontrado.</p>
              <p className="text-slate-600 text-xs mt-1">Clientes aparecem aqui quando se cadastram na plataforma.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

//  Health Check tab

function HealthCard({ name, service }) {
  const cfg = HEALTH_STATUS[service.status] || HEALTH_STATUS.error;
  const Icon = cfg.icon;
  return (
    <div className={`p-5 rounded-xl border ${cfg.bg} ${cfg.border}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-slate-100">{service.label || name}</span>
        <Icon className={`w-5 h-5 ${cfg.color}`} />
      </div>
      <div className={`text-xs font-semibold uppercase tracking-wide ${cfg.color}`}>{cfg.label}</div>
      {service.detail && (
        <div className="text-xs text-slate-400 mt-1.5">{service.detail}</div>
      )}
      {service.reason && (
        <div className="text-xs text-red-400/80 mt-1 font-mono break-all" title={service.reason}>
          {service.reason.length > 80 ? service.reason.slice(0, 80) + "" : service.reason}
        </div>
      )}
    </div>
  );
}

function HealthTab() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/health");
      setHealth(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao carregar health check.");
      toast.error("Falha ao buscar status dos serviços.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
        <p className="text-slate-500 text-sm">Verificando serviços...</p>
      </div>
    );
  }

  if (error || !health) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <XCircle className="w-10 h-10 text-red-400" />
        <p className="text-slate-400 text-sm">{error || "Erro desconhecido"}</p>
        <button onClick={load} className="text-xs text-slate-400 hover:text-slate-100 flex items-center gap-1 mt-2">
          <RefreshCw className="w-3 h-3" /> Tentar novamente
        </button>
      </div>
    );
  }

  const overallCfg = HEALTH_STATUS[health.overall] || HEALTH_STATUS.error;
  const OverallIcon = overallCfg.icon;

  const overallMessages = {
    ok:      "Todos os serviços estão operacionais",
    warning: "Atenção - alguns serviços precisam de verificação",
    error:   "Falha detectada em um ou mais serviços",
  };

  return (
    <div>
      {/* Overall banner */}
      <div className={`flex items-center gap-3 px-5 py-4 rounded-xl border mb-6 ${overallCfg.bg} ${overallCfg.border}`}>
        <OverallIcon className={`w-6 h-6 shrink-0 ${overallCfg.color}`} />
        <div className="flex-1 min-w-0">
          <div className={`font-semibold ${overallCfg.color}`}>{overallMessages[health.overall]}</div>
          {health.checked_at && (
            <div className="text-xs text-slate-500 mt-0.5">
              ltima verificação: {new Date(health.checked_at).toLocaleString("pt-BR")}
            </div>
          )}
        </div>
        <button
          onClick={load}
          className="ml-auto text-xs text-slate-400 hover:text-slate-100 flex items-center gap-1 shrink-0"
        >
          <RefreshCw className="w-3 h-3" /> Atualizar
        </button>
      </div>

      {/* Service cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(health.services || {}).map(([key, service]) => (
          <HealthCard key={key} name={key} service={service} />
        ))}
      </div>
    </div>
  );
}

//  Auditoria tab

function AuditoriaTab({ clientes }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const clientesByTenantId = useMemo(() => {
    return clientes.reduce((acc, cliente) => {
      if (cliente.tenant_id) acc[cliente.tenant_id] = cliente;
      return acc;
    }, {});
  }, [clientes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/audit?limit=100");
      setLogs(res.data);
    } catch {
      toast.error("Erro ao carregar auditoria.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
        <h2 className="font-medium flex items-center gap-2 text-slate-100">
          <ClipboardList className="w-4 h-4 text-slate-400" />
          Atividades administrativas recentes
        </h2>
        <button
          onClick={load}
          className="text-xs text-slate-400 hover:text-slate-100 flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Atualizar
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-16">
          <ClipboardList className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Nenhuma atividade registrada ainda.</p>
          <p className="text-slate-600 text-xs mt-1">Ações como ativar, bloquear e cancelar clientes aparecem aqui.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-800/50">
          {logs.map((log) => {
            const label = ACTION_LABELS[log.action] || log.action;
            const cliente = log.target_tenant_id ? clientesByTenantId[log.target_tenant_id] : null;
            const clienteNome = log.target_name || cliente?.company_name || cliente?.owner_name || "Cliente no identificado";
            const ownerEmail = log.target_email || cliente?.owner_email || "Owner no informado";
            const statusColors = {
              "tenant.status.active":    "text-green-400",
              "tenant.status.blocked":   "text-red-400",
              "tenant.status.cancelled": "text-slate-400",
              "tenant.status.inactive":  "text-yellow-400",
              "tenant.status.trial":     "text-blue-400",
              "tenant.plan.changed":     "text-amber-400",
              "tenant.cancelled":        "text-slate-400",
            };
            const color = statusColors[log.action] || "text-slate-300";
            return (
              <div key={log.id} className="px-6 py-4 flex items-start gap-4 hover:bg-slate-800/20 transition-colors">
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${color.replace("text-", "bg-")}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${color}`}>{label}</span>
                    {log.after?.plan && log.before?.plan && (
                      <span className="text-xs text-slate-500">
                        {log.before.plan}  {log.after.plan}
                      </span>
                    )}
                  </div>
                  <div className="grid gap-1 text-xs text-slate-500 mt-2 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <span className="text-slate-600">Cliente: </span>
                      <span className="text-slate-300">{clienteNome}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Owner: </span>
                      <span className="text-slate-300 break-all">{ownerEmail}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Realizado por: </span>
                      <span className="text-slate-300 break-all">{log.actor_email || "Usuário não informado"}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Data: </span>
                      <span className="text-slate-300">{log.created_at ? new Date(log.created_at).toLocaleString("pt-BR") : ""}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

//  Main page


// -- Solicitacoes tab ---------------------------------------------------------

function DemoStatusBadge({ status }) {
  const cfg = DEMO_STATUS_CONFIG[status] || DEMO_STATUS_CONFIG.novo;
  return (
    <span className={"inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border " + cfg.color + " " + cfg.bg + " " + cfg.border}>
      {cfg.label}
    </span>
  );
}

function DemoRequestsTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [selected, setSelected] = useState(null);
  const [accessModal, setAccessModal] = useState(null);
  const [successAccess, setSuccessAccess] = useState(null);
  const [creatingAccess, setCáreatingAccess] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filters, setFilters] = useState({ search: "", status: "", source: "", date_from: "", date_to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, skip: 0 };
      Object.entries(filters).forEach(([key, value]) => { if (value) params[key] = value; });
      const { data } = await api.get("/admin/demo-requests", { params });
      setItems(data.items || []);
      setStats(data.stats || {});
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar solicitacoes.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (requestId, status) => {
    if (status === "convertido") {
      toast.info("Funcionalidade em desenvolvimento");
      return;
    }
    setUpdating(requestId);
    try {
      const { data } = await api.patch("/admin/demo-requests/" + requestId, { status });
      toast.success("Status atualizado.");
      setItems((current) => current.map((item) => item.id === requestId ? data.demo_request : item));
      if (selected?.id === requestId) setSelected(data.demo_request);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar status.");
    } finally {
      setUpdating(null);
    }
  };



  const generateTemporaryPassword = () => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
    const bytes = new Uint32Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join("");
  };

  const openCreateAccess = (request) => {
    setAccessModal({
      request,
      company_name: request.company || request.name || "",
      plan: "free",
      user_limit: 5,
      channel_limit: 2,
      temporary_password: generateTemporaryPassword(),
    });
  };

  const copyText = async (text, label = "Copiado") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const confirmCreateAccess = async () => {
    if (!accessModal?.request?.id) return;
    setCáreatingAccess(true);
    try {
      const payload = {
        company_name: accessModal.company_name,
        plan: accessModal.plan,
        user_limit: Number(accessModal.user_limit || 5),
        channel_limit: Number(accessModal.channel_limit || 2),
        temporary_password: accessModal.temporary_password,
      };
      const { data } = await api.post("/admin/demo-requests/" + accessModal.request.id + "/create-access", payload);
      const success = {
        login_url: data.login_url || "https://app.clivercrm.com.br/login",
        email: data.email || accessModal.request.email,
        temporary_password: accessModal.temporary_password,
        tenant_id: data.tenant_id,
        email_sent: Boolean(data.email_sent),
        email_message: data.email_message,
      };
      toast.success("Acesso criado com sucesso.");
      setItems((current) => current.map((item) => item.id === accessModal.request.id ? data.demo_request : item));
      setSelected(data.demo_request);
      setAccessModal(null);
      setSuccessAccess(success);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar acesso.");
    } finally {
      setCáreatingAccess(false);
    }
  };

  const deleteRequest = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await api.delete("/admin/demo-requests/" + deleteTarget.id);
      toast.success("Solicitacao excluida.");
      setSelected(null);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir solicitação.");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (value) => value ? new Date(value).toLocaleString("pt-BR") : "-";
  const statusOptions = Object.entries(DEMO_STATUS_CONFIG);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total solicitações" value={stats?.total ?? 0} />
        <StatCard label="Novas" value={stats?.novo ?? 0} color="text-blue-400" />
        <StatCard label="Em contato" value={stats?.em_contato ?? 0} color="text-yellow-400" />
        <StatCard label="Aprovadas" value={stats?.aprovado ?? 0} color="text-green-400" />
        <StatCard label="Convertidas" value={stats?.convertido ?? 0} color="text-cyan-400" />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800 space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <h2 className="font-medium flex items-center gap-2 text-slate-100">
              <ClipboardList className="w-4 h-4 text-slate-400" />
              Solicitacoes de Acesso ({total})
            </h2>
            <button onClick={load} className="text-xs text-slate-400 hover:text-slate-100 transition-colors flex items-center gap-1 w-fit">
              <RefreshCw className="w-3 h-3" /> Atualizar
            </button>
          </div>

          <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
            <label className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Buscar nome, email ou empresa" className="w-full h-9 rounded-lg border border-slate-800 bg-slate-950 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-slate-600" />
            </label>
            <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="h-9 rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-300 focus:outline-none focus:border-slate-600">
              <option value="">Todos os status</option>
              {statusOptions.map(([value, cfg]) => <option key={value} value={value}>{cfg.label}</option>)}
            </select>
            <input value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))} placeholder="Origem" className="h-9 rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-slate-600" />
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="h-9 rounded-lg border border-slate-800 bg-slate-950 px-3 text-xs text-slate-300 focus:outline-none focus:border-slate-600" />
              <input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="h-9 rounded-lg border border-slate-800 bg-slate-950 px-3 text-xs text-slate-300 focus:outline-none focus:border-slate-600" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3"><Loader2 className="w-8 h-8 animate-spin text-slate-500" /><p className="text-slate-500 text-sm">Carregando solicitacoes...</p></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16"><ClipboardList className="w-10 h-10 text-slate-700 mx-auto mb-3" /><p className="text-slate-500 text-sm">Nenhuma solicitação encontrada.</p><p className="text-slate-600 text-xs mt-1">As solicitacoes da landing aparecerao aqui.</p></div>
        ) : (
          <div className="max-h-[calc(100vh-360px)] min-h-[320px] overflow-auto"><table className="min-w-[1120px] w-full text-sm"><thead className="sticky top-0 z-10 bg-slate-900"><tr className="text-slate-500 text-xs border-b border-slate-800 uppercase tracking-wide shadow-[0_1px_0_rgba(30,41,59,0.9)]"><th className="text-left px-5 py-3">Nome</th><th className="text-left px-5 py-3">Empresa</th><th className="text-left px-5 py-3">E-mail</th><th className="text-left px-5 py-3">WhatsApp</th><th className="text-left px-5 py-3">Atendentes</th><th className="text-left px-5 py-3">Origem</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Data</th><th className="text-left px-5 py-3">UTM source</th><th className="text-left px-5 py-3">UTM campaign</th><th className="text-left px-5 py-3"></th></tr></thead><tbody className="divide-y divide-slate-800/50">{items.map((item) => <tr key={item.id} onClick={() => setSelected(item)} className="cursor-pointer hover:bg-slate-800/30 transition-colors"><td className="px-5 py-4 font-medium text-slate-100">{item.name || "-"}</td><td className="px-5 py-4 text-slate-300">{item.company || "-"}</td><td className="px-5 py-4 text-slate-400 break-all">{item.email}</td><td className="px-5 py-4 text-slate-300">{item.phone || "-"}</td><td className="px-5 py-4 text-slate-300">{item.team_size || "-"}</td><td className="px-5 py-4 text-slate-400">{item.source || "-"}</td><td className="px-5 py-4"><DemoStatusBadge status={item.status} /></td><td className="px-5 py-4 text-slate-500 text-xs">{formatDate(item.created_at)}</td><td className="px-5 py-4 text-slate-500">{item.utm_source || "-"}</td><td className="px-5 py-4 text-slate-500">{item.utm_campaign || "-"}</td><td className="px-5 py-4"><Eye className="w-4 h-4 text-slate-500" /></td></tr>)}</tbody></table></div>
        )}
      </div>

      <ConfirmDeleteModal open={Boolean(deleteTarget)} title="Excluir solicitação" description="Tem certeza que deseja excluir esta solicitação? Essa acao não pode ser desfeita." expectedText={["rejeitado", "convertido"].includes(deleteTarget?.status) ? "EXCLUIR" : (deleteTarget?.name || "EXCLUIR")} onClose={() => setDeleteTarget(null)} onConfirm={deleteRequest} busy={deleting} />

      {selected && <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setSelected(null)}><aside className="h-full w-full max-w-xl overflow-y-auto border-l border-slate-800 bg-slate-950 shadow-2xl" onClick={(e) => e.stopPropagation()}><div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-800 bg-slate-950/95 px-6 py-5 backdrop-blur"><div><h3 className="text-lg font-semibold text-slate-100">{selected.name}</h3><p className="text-sm text-slate-500">{selected.company || "Empresa não informada"}</p></div><button onClick={() => setSelected(null)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-900 hover:text-slate-100"><X className="w-5 h-5" /></button></div><div className="space-y-6 px-6 py-5"><div className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-4"><div><p className="text-xs uppercase tracking-wider text-slate-600">Status atual</p><div className="mt-1"><DemoStatusBadge status={selected.status} /></div></div>{updating === selected.id && <Loader2 className="w-5 h-5 animate-spin text-slate-500" />}</div><div className="grid gap-3 sm:grid-cols-2"><DetailItem icon={Mail} label="E-mail" value={selected.email} /><DetailItem icon={Phone} label="Telefone" value={selected.phone || "-"} /><DetailItem icon={Users} label="Atendentes" value={selected.team_size || "-"} /><DetailItem icon={CalendarDays} label="Criado em" value={formatDate(selected.created_at)} /></div><section><p className="mb-2 text-xs uppercase tracking-wider text-slate-600">Mensagem</p><div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm leading-relaxed text-slate-300">{selected.message || "Sem mensagem."}</div></section><section className="grid gap-3 text-sm"><DetailRow label="Origem" value={selected.source} /><DetailRow label="UTM source" value={selected.utm_source} /><DetailRow label="UTM medium" value={selected.utm_medium} /><DetailRow label="UTM campaign" value={selected.utm_campaign} /><DetailRow label="Page URL" value={selected.page_url} /><DetailRow label="IP" value={selected.ip} /><DetailRow label="User agent" value={selected.user_agent} /></section><section><p className="mb-3 text-xs uppercase tracking-wider text-slate-600">Ações</p><div className="grid grid-cols-1 gap-2 sm:grid-cols-2"><ActionButton icon={Clock} label="Marcar em contato" onClick={() => updateStatus(selected.id, "em_contato")} /><ActionButton icon={CalendarDays} label="Agendar demo" onClick={() => updateStatus(selected.id, "demo_agendada")} /><ActionButton icon={Check} label="Aprovar" onClick={() => updateStatus(selected.id, "aprovado")} /><ActionButton icon={XCircle} label="Rejeitar" onClick={() => updateStatus(selected.id, "rejeitado")} danger /><ActionButton icon={KeyRound} label="Criar acesso" onClick={() => openCreateAccess(selected)} /><ActionButton icon={Trash2} label="Excluir solicitação" onClick={() => setDeleteTarget(selected)} danger /></div></section></div></aside></div>}


      {accessModal && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4" onClick={() => setAccessModal(null)}><div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl" onClick={(e) => e.stopPropagation()}><div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-5"><div><h3 className="text-lg font-semibold text-slate-100">Criar acesso</h3><p className="text-sm text-slate-500">Revise os dados antes de criar o workspace.</p></div><button onClick={() => setAccessModal(null)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-900 hover:text-slate-100"><X className="w-5 h-5" /></button></div><div className="space-y-4 px-6 py-5"><div className="grid gap-3 sm:grid-cols-2"><DetailItem icon={Users} label="Nome" value={accessModal.request.name} /><DetailItem icon={Mail} label="E-mail" value={accessModal.request.email} /><DetailItem icon={Phone} label="WhatsApp" value={accessModal.request.phone || "-"} /><DetailItem icon={Building2} label="Empresa" value={accessModal.request.company || "-"} /></div><label className="block"><span className="mb-1 block text-xs uppercase tracking-wider text-slate-600">Nome da empresa</span><input value={accessModal.company_name} onChange={(e) => setAccessModal((m) => ({ ...m, company_name: e.target.value }))} className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900 px-3 text-sm text-slate-100 focus:outline-none focus:border-slate-600" /></label><div className="grid gap-3 sm:grid-cols-3"><label><span className="mb-1 block text-xs uppercase tracking-wider text-slate-600">Plano inicial</span><select value={accessModal.plan} onChange={(e) => setAccessModal((m) => ({ ...m, plan: e.target.value }))} className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900 px-3 text-sm text-slate-100"><option value="free">Free</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option></select></label><label><span className="mb-1 block text-xs uppercase tracking-wider text-slate-600">Limite usuários</span><input type="number" min="1" value={accessModal.user_limit} onChange={(e) => setAccessModal((m) => ({ ...m, user_limit: e.target.value }))} className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900 px-3 text-sm text-slate-100" /></label><label><span className="mb-1 block text-xs uppercase tracking-wider text-slate-600">Limite canais</span><input type="number" min="1" value={accessModal.channel_limit} onChange={(e) => setAccessModal((m) => ({ ...m, channel_limit: e.target.value }))} className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900 px-3 text-sm text-slate-100" /></label></div><div><span className="mb-1 block text-xs uppercase tracking-wider text-slate-600">Senha temporária</span><div className="flex gap-2"><input readOnly value={accessModal.temporary_password} className="h-10 flex-1 rounded-lg border border-slate-800 bg-slate-900 px-3 font-mono text-sm text-slate-100" /><button onClick={() => copyText(accessModal.temporary_password, "Senha copiada")} className="rounded-lg border border-slate-700 bg-slate-900 px-3 text-slate-200 hover:bg-slate-800"><Copy className="w-4 h-4" /></button></div></div></div><div className="flex justify-end gap-2 border-t border-slate-800 px-6 py-4"><button onClick={() => setAccessModal(null)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-900">Cancelar</button><button onClick={confirmCreateAccess} disabled={creatingAccess} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">{creatingAccess ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Criar acesso</button></div></div></div>}

      {successAccess && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 px-4"><div className="w-full max-w-md rounded-2xl border border-green-400/30 bg-slate-950 shadow-2xl"><div className="border-b border-slate-800 px-6 py-5"><h3 className="text-lg font-semibold text-green-400">Acesso criado</h3><p className={"text-sm " + (successAccess.email_sent ? "text-green-400" : "text-amber-400")}>{successAccess.email_message || (successAccess.email_sent ? "E-mail enviado com sucesso para " + successAccess.email : "Acesso criado, mas o e-mail não foi enviado. Copie os dados abaixo e envie manualmente.")}</p></div><div className="space-y-3 px-6 py-5 text-sm"><DetailRow label="URL" value={successAccess.login_url} /><DetailRow label="E-mail" value={successAccess.email} /><DetailRow label="Senha" value={successAccess.temporary_password} /></div><div className="flex justify-end gap-2 border-t border-slate-800 px-6 py-4"><button onClick={() => copyText('URL:\n' + successAccess.login_url + '\n\nE-mail:\n' + successAccess.email + '\n\nSenha temporária:\n' + successAccess.temporary_password, "Acesso copiado")} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-900"><Copy className="w-4 h-4" /> Copiar acesso</button><button onClick={() => setSuccessAccess(null)} className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-white">Fechar</button></div></div></div>}

    </>
  );
}

function DetailItem({ icon: Icon, label, value }) {
  return <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4"><div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-600"><Icon className="w-3.5 h-3.5" /> {label}</div><p className="break-words text-sm text-slate-200">{value || "-"}</p></div>;
}

function DetailRow({ label, value }) {
  return <div className="grid gap-1 border-b border-slate-900 pb-2 sm:grid-cols-[120px_1fr]"><span className="text-xs uppercase tracking-wider text-slate-600">{label}</span><span className="break-all text-slate-300">{value || "-"}</span></div>;
}

function ActionButton({ icon: Icon, label, onClick, danger = false, className = "" }) {
  return <button onClick={onClick} className={"flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition " + (danger ? "border-red-400/30 bg-red-400/10 text-red-400 hover:bg-red-400/20" : "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800") + " " + className}><Icon className="w-4 h-4" /> {label}</button>;
}

export default function Admin() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.pathname === "/admin/demo-requests" ? "solicitacoes" : "clientes");

  const loadData = useCallback(async () => {
    try {
      const [clientesRes, statsRes] = await Promise.all([
        api.get("/admin/tenants"),
        api.get("/admin/stats"),
      ]);
      setClientes(clientesRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Erro ao carregar painel SaaS.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (location.pathname === "/admin/demo-requests" && activeTab !== "solicitacoes") {
      setActiveTab("solicitacoes");
    }
  }, [location.pathname, activeTab]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-950">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Carregando painel SaaS...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "clientes",   label: "Clientes",    icon: Building2     },
    { id: "health",     label: "Health Check", icon: Activity      },
    { id: "auditoria",  label: "Auditoria",    icon: ClipboardList },
    { id: "solicitacoes", label: "Solicitacoes", icon: ClipboardList },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
            <LayoutDashboard className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Painel SaaS</h1>
            <p className="text-slate-400 text-sm mt-0.5">Gerenciamento global de clientes</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-amber-500/70 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded">
            <Crown className="w-3 h-3" />
            {user?.email}
          </div>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-slate-400 hover:text-slate-100 transition-colors"
          >
             Voltar ao CRM
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tab bar */}
        <div className="flex gap-1 mb-6 bg-slate-900 border border-slate-800 rounded-xl p-1 w-fit">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === id
                  ? "bg-slate-800 text-slate-100 shadow"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === "clientes" && (
          <ClientesTab stats={stats} clientes={clientes} onReload={loadData} />
        )}
        {activeTab === "health" && <HealthTab />}
        {activeTab === "auditoria" && <AuditoriaTab clientes={clientes} />}
        {activeTab === "solicitacoes" && <DemoRequestsTab />}
      </div>
    </div>
  );
}
